import { useEffect, useState, useMemo } from 'react'
import { customerTrackingApi } from '../../services/api'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'
import ExportBar, { ExportColumn } from '../../components/ExportBar'

interface CustomerTracking {
  id: number
  seller_id: number
  seller_name: string
  name: string
  nickname: string | null
  district: string | null
  address: string | null
  phone: string | null
  cycle_days: number
  last_sale_date: string | null
  last_sale_amount: number
  next_expected_date: string | null
  days_since_last_sale: number | null
  status: 'active' | 'inactive'
}

type FilterStatus = 'all' | 'active' | 'inactive'

export default function TrackingPage() {
  const [customers, setCustomers] = useState<CustomerTracking[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<FilterStatus>('all')

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await customerTrackingApi.getTracking()
        setCustomers(res.data.customers || [])
      } catch (err) {
        console.error('Error fetching tracking data:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const filtered = useMemo(() => {
    if (filter === 'all') return customers
    return customers.filter((c) => c.status === filter)
  }, [customers, filter])

  const { sortedData: sortedFiltered, sortConfig, requestSort } = useSortableData(filtered)

  const summary = useMemo(() => {
    const active = customers.filter((c) => c.status === 'active').length
    const inactive = customers.filter((c) => c.status === 'inactive').length
    const overdue = customers.filter((c) => {
      if (!c.next_expected_date) return false
      return new Date(c.next_expected_date) < new Date()
    }).length
    return { total: customers.length, active, inactive, overdue }
  }, [customers])

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString()
  }

  const getStatusColor = (status: string) => {
    return status === 'active'
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800'
  }

  const getOverdueColor = (nextDate: string | null) => {
    if (!nextDate) return ''
    const next = new Date(nextDate)
    const today = new Date()
    if (next < today) return 'text-red-600 font-semibold'
    const daysUntil = Math.ceil((next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    if (daysUntil <= 3) return 'text-terracotta-700 font-semibold'
    return 'text-espresso-800'
  }

  const filterLabels: Record<FilterStatus, string> = {
    all: 'Todos',
    active: 'Activos',
    inactive: 'Inactivos',
  }

  const statusLabels: Record<string, string> = {
    active: 'activo',
    inactive: 'inactivo',
  }

  const exportColumns = useMemo<ExportColumn<CustomerTracking>[]>(
    () => [
      { key: 'name', label: 'Cliente', format: (c) => `${c.name}${c.nickname ? ` (${c.nickname})` : ''}` },
      { key: 'district', label: 'Distrito', format: (c) => c.district || '-' },
      { key: 'address', label: 'Dirección', format: (c) => c.address || '-' },
      {
        key: 'last_sale_date',
        label: 'Última Venta',
        format: (c) => `${formatDate(c.last_sale_date)}${c.last_sale_amount > 0 ? ` ($${c.last_sale_amount.toFixed(2)})` : ''}`,
      },
      { key: 'cycle_days', label: 'Ciclo', format: (c) => `${c.cycle_days} días` },
      { key: 'next_expected_date', label: 'Próxima Esperada', format: (c) => formatDate(c.next_expected_date) },
      { key: 'days_since_last_sale', label: 'Días Desde', format: (c) => (c.days_since_last_sale !== null ? `${c.days_since_last_sale}d` : 'Nunca') },
      { key: 'status', label: 'Estado', format: (c) => statusLabels[c.status] || c.status },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-6">Seguimiento de Pedidos de Clientes</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-cream-50 shadow rounded-lg p-4">
          <div className="text-sm font-medium text-espresso-500">Total de Clientes</div>
          <div className="mt-1 text-2xl font-bold text-espresso-800">{summary.total}</div>
        </div>
        <div className="bg-cream-50 shadow rounded-lg p-4">
          <div className="text-sm font-medium text-espresso-500">Activos</div>
          <div className="mt-1 text-2xl font-bold text-green-600">{summary.active}</div>
        </div>
        <div className="bg-cream-50 shadow rounded-lg p-4">
          <div className="text-sm font-medium text-espresso-500">Inactivos (3+ meses)</div>
          <div className="mt-1 text-2xl font-bold text-red-600">{summary.inactive}</div>
        </div>
        <div className="bg-cream-50 shadow rounded-lg p-4">
          <div className="text-sm font-medium text-espresso-500">Pedidos Atrasados</div>
          <div className="mt-1 text-2xl font-bold text-terracotta-700">{summary.overdue}</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-2 mb-6">
        {(['all', 'active', 'inactive'] as FilterStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              filter === status
                ? 'bg-terracotta-600 text-white'
                : 'bg-white text-espresso-700 border border-espresso-300 hover:bg-cream-50'
            }`}
          >
            {filterLabels[status]}
            {status !== 'all' && (
              <span className="ml-1">
                ({status === 'active' ? summary.active : summary.inactive})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tracking Table */}
      {filtered.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay clientes que coincidan con el filtro seleccionado.</p>
        </div>
      ) : (
        <>
          <ExportBar data={filtered} columns={exportColumns} filename="seguimiento_clientes" title="Seguimiento de Pedidos de Clientes" />
          <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Cliente" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Distrito" sortKey="district" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Dirección" sortKey="address" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Última Venta" sortKey="last_sale_date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Ciclo" sortKey="cycle_days" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Próxima Esperada" sortKey="next_expected_date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Días Desde" sortKey="days_since_last_sale" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedFiltered.map((c) => (
                  <tr key={c.id} className="hover:bg-cream-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-espresso-800">{c.name}</div>
                      {c.nickname && <div className="text-xs text-espresso-500">{c.nickname}</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800">
                      {c.district || '-'}
                    </td>
                    <td
                      className="px-4 py-3 max-w-[220px] truncate text-sm text-espresso-800"
                      title={c.address || undefined}
                    >
                      {c.address || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800">
                      {formatDate(c.last_sale_date)}
                      {c.last_sale_amount > 0 && (
                        <div className="text-xs text-espresso-500">
                          ${c.last_sale_amount.toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800">
                      {c.cycle_days} días
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${getOverdueColor(c.next_expected_date)}`}>
                      {formatDate(c.next_expected_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800">
                      {c.days_since_last_sale !== null ? `${c.days_since_last_sale}d` : 'Nunca'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(c.status)}`}>
                        {statusLabels[c.status] || c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}
    </div>
  )
}
